const EYE_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M2.2 12s3.4-6 9.8-6 9.8 6 9.8 6-3.4 6-9.8 6-9.8-6-9.8-6Z"></path>
    <circle cx="12" cy="12" r="2.8"></circle>
  </svg>
`;

const VELUXO_VARIANT_COPY_SCOPE = '#quick-add-modal-content, .cart-items__variants';

function normalizeVeluxoVariantText(value = '') {
  return value
    .replace(/Tamaño|Tamano/g, 'Size')
    .replace(/Full Bottle/gi, '100 ml / 3.4oz');
}

function normalizeVeluxoVariantCopy(root = document) {
  const scopes = [];

  if (root instanceof Element && root.matches(VELUXO_VARIANT_COPY_SCOPE)) {
    scopes.push(root);
  }

  root.querySelectorAll?.(VELUXO_VARIANT_COPY_SCOPE).forEach((scope) => scopes.push(scope));

  scopes.forEach((scope) => {
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    let node;

    while ((node = walker.nextNode())) {
      const nextValue = normalizeVeluxoVariantText(node.nodeValue || '');
      if (nextValue !== node.nodeValue) node.nodeValue = nextValue;
    }

    scope.querySelectorAll('[aria-label], [title]').forEach((element) => {
      ['aria-label', 'title'].forEach((attribute) => {
        if (!element.hasAttribute(attribute)) return;
        const currentValue = element.getAttribute(attribute) || '';
        const nextValue = normalizeVeluxoVariantText(currentValue);
        if (nextValue !== currentValue) element.setAttribute(attribute, nextValue);
      });
    });
  });
}

function getProductInfo(card) {
  if (card.matches('product-card')) {
    const link =
      card.querySelector('[ref="productCardLink"]') ||
      card.querySelector('a[href*="/products/"]');

    return {
      url: link?.href,
      title: link?.textContent?.trim() || 'product',
    };
  }

  if (card.matches('.vx-product')) {
    const link =
      card.querySelector('.vx-product__img') ||
      card.querySelector('a[href*="/products/"]');
    const name = card.querySelector('.vx-product__name');

    return {
      url: link?.href,
      title: name?.textContent?.trim() || 'product',
    };
  }

  return {};
}

function enhanceCard(card) {
  if (!(card instanceof HTMLElement)) return;
  if (card.querySelector(':scope .vlx-quick-view-trigger')) return;

  const { url, title } = getProductInfo(card);
  if (!url) return;

  const quickView = document.createElement('quick-add-component');
  quickView.className = 'vlx-quick-view-trigger';
  quickView.dataset.productUrl = url;
  quickView.dataset.productTitle = title;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'vlx-quick-view-button';
  button.setAttribute('aria-label', `Quick view ${title}`);
  button.setAttribute('title', `Quick view ${title}`);
  button.innerHTML = EYE_ICON;

  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (typeof quickView.handleClick === 'function') {
      quickView.handleClick(event);
    }
  });

  quickView.appendChild(button);

  const target = card.matches('product-card')
    ? card.querySelector('.card-gallery') || card.querySelector('.product-card__content')
    : card;

  (target || card).appendChild(quickView);
}

function enhanceAll(root = document) {
  if (root.matches?.('product-card, .vx-product')) enhanceCard(root);
  root.querySelectorAll?.('product-card, .vx-product').forEach(enhanceCard);
}

function normalizeImageKey(src) {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.delete('width');
    return `${url.origin}${url.pathname}`;
  } catch {
    return src;
  }
}

function buildQuickViewGallery(root = document) {
  const modalContent = root.matches?.('#quick-add-modal-content')
    ? root
    : root.querySelector?.('#quick-add-modal-content');

  if (!(modalContent instanceof HTMLElement)) return;
  if (modalContent.querySelector('.vlx-qv-gallery')) return;

  const source = modalContent.querySelector('.product-information__media');
  if (!(source instanceof HTMLElement)) return;

  const images = [];
  const seen = new Set();

  source.querySelectorAll('img').forEach((img) => {
    const src = img.currentSrc || img.getAttribute('src');
    if (!src) return;

    const key = normalizeImageKey(src);
    if (seen.has(key)) return;
    seen.add(key);

    images.push({
      src,
      srcset: img.getAttribute('srcset') || '',
      alt: img.getAttribute('alt') || '',
    });
  });

  if (!images.length) return;

  const gallery = document.createElement('div');
  gallery.className = 'vlx-qv-gallery';

  const main = document.createElement('div');
  main.className = 'vlx-qv-gallery__main';

  const mainImg = document.createElement('img');
  mainImg.src = images[0].src;
  mainImg.alt = images[0].alt;
  mainImg.loading = 'eager';
  if (images[0].srcset) mainImg.srcset = images[0].srcset;
  main.appendChild(mainImg);

  const thumbs = document.createElement('div');
  thumbs.className = 'vlx-qv-gallery__thumbs';
  thumbs.setAttribute('aria-label', 'Product images');

  images.forEach((image, index) => {
    const thumb = document.createElement('button');
    thumb.type = 'button';
    thumb.className = 'vlx-qv-gallery__thumb';
    thumb.classList.toggle('is-active', index === 0);
    thumb.setAttribute('aria-label', `View product image ${index + 1}`);

    const thumbImg = document.createElement('img');
    thumbImg.src = image.src;
    thumbImg.alt = '';
    thumbImg.loading = 'lazy';
    thumb.appendChild(thumbImg);

    thumb.addEventListener('click', () => {
      mainImg.src = image.src;
      mainImg.alt = image.alt;
      if (image.srcset) mainImg.srcset = image.srcset;
      else mainImg.removeAttribute('srcset');

      thumbs.querySelectorAll('.vlx-qv-gallery__thumb').forEach((button) => {
        button.classList.remove('is-active');
      });
      thumb.classList.add('is-active');
    });

    thumbs.appendChild(thumb);
  });

  gallery.append(main, thumbs);
  source.insertAdjacentElement('beforebegin', gallery);
  source.classList.add('vlx-qv-original-media');
}

normalizeVeluxoVariantCopy();

customElements.whenDefined('quick-add-component').then(() => {
  enhanceAll();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        enhanceAll(node);
      }
    }

    buildQuickViewGallery();
    normalizeVeluxoVariantCopy();
  });

  observer.observe(document.body, { childList: true, subtree: true });
  buildQuickViewGallery();
  normalizeVeluxoVariantCopy();
});

/* VELUXO_HOME_HOVER_GALLERY_V3
   Homepage product cards: reveal the second image within one second, then move
   directly through later product images every two seconds. Once the alternate
   gallery is visible it stays opaque during swaps, preventing the featured image
   from flashing between image 2, 3, 4, etc. Desktop/fine-pointer only. */
(() => {
  const HOVER_DELAY = 850;
  const IMAGE_INTERVAL = 2000;
  const FADE_MS = 260;
  const ZOOM_MS = 1550;
  const ZOOM_SCALE = 1.06;
  const imageCache = new Map();

  if (!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

  function productJsonUrl(productUrl) {
    try {
      const url = new URL(productUrl, window.location.origin);
      url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch {
      return null;
    }
  }

  function normalizeShopifyImage(src) {
    if (!src) return '';
    if (src.startsWith('//')) return `https:${src}`;
    try {
      return new URL(src, window.location.origin).toString();
    } catch {
      return src;
    }
  }

  async function getImages(productUrl) {
    if (imageCache.has(productUrl)) return imageCache.get(productUrl);

    const requestUrl = productJsonUrl(productUrl);
    if (!requestUrl) return [];

    const promise = fetch(requestUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product images request failed: ${response.status}`);
        return response.json();
      })
      .then((product) => {
        const seen = new Set();
        return (product.images || [])
          .map(normalizeShopifyImage)
          .filter(Boolean)
          .filter((src) => {
            const key = normalizeImageKey(src);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
      })
      .catch(() => []);

    imageCache.set(productUrl, promise);
    return promise;
  }

  function initCard(card) {
    if (!(card instanceof HTMLElement) || card.dataset.vlxHoverGalleryReady === 'true') return;

    const media = card.querySelector('.vx-product__img');
    const baseImage = media?.querySelector('img');
    const productUrl = media?.href || card.querySelector('a[href*="/products/"]')?.href;
    if (!(media instanceof HTMLElement) || !(baseImage instanceof HTMLImageElement) || !productUrl) return;

    card.dataset.vlxHoverGalleryReady = 'true';
    media.style.position = 'relative';
    media.style.overflow = 'hidden';

    let hoverTimer = null;
    let cycleTimer = null;
    let overlay = null;
    let activeImages = [];
    let index = 0;
    let session = 0;
    let swapToken = 0;
    let hasShownAlternate = false;

    function clearTimers() {
      if (hoverTimer) window.clearTimeout(hoverTimer);
      if (cycleTimer) window.clearInterval(cycleTimer);
      hoverTimer = null;
      cycleTimer = null;
    }

    function removeOverlay() {
      if (!overlay) return;
      overlay.style.opacity = '0';
      overlay.style.transform = 'scale(1.012)';
      const oldOverlay = overlay;
      overlay = null;
      window.setTimeout(() => oldOverlay.remove(), FADE_MS + 50);
    }

    function reset() {
      session += 1;
      swapToken += 1;
      clearTimers();
      activeImages = [];
      index = 0;
      hasShownAlternate = false;
      removeOverlay();
    }

    function ensureOverlay() {
      if (overlay) return overlay;

      overlay = document.createElement('img');
      overlay.className = 'vlx-home-hover-gallery-image';
      overlay.alt = baseImage.alt || '';
      overlay.decoding = 'async';
      Object.assign(overlay.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        padding: '1.5%',
        objectFit: 'contain',
        objectPosition: 'center',
        background: 'inherit',
        opacity: '0',
        transform: 'scale(1.012)',
        transformOrigin: '50% 50%',
        transition: `opacity ${FADE_MS}ms ease, transform ${ZOOM_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`,
        willChange: 'opacity, transform',
        pointerEvents: 'none',
        zIndex: '1',
      });
      media.appendChild(overlay);
      return overlay;
    }

    async function showImage(src) {
      const image = ensureOverlay();
      const mySwap = ++swapToken;

      // Decode each upcoming image before changing the visible overlay. This keeps
      // the current alternate image covering the featured image during the swap.
      const preload = new Image();
      preload.decoding = 'async';
      preload.src = src;
      try {
        await preload.decode();
      } catch {
        // Browsers may reject decode for a cached image; assigning the source is
        // still safe and keeps the existing overlay opaque until this point.
      }

      if (mySwap !== swapToken || !overlay || overlay !== image || !card.matches(':hover')) return;

      if (!hasShownAlternate) {
        image.src = src;
        image.style.opacity = '0';
        image.style.transform = 'scale(1.012)';
        hasShownAlternate = true;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (mySwap !== swapToken || !overlay || overlay !== image) return;
            image.style.opacity = '1';
            image.style.transform = `scale(${ZOOM_SCALE})`;
          });
        });
        return;
      }

      // For image 3 onward, NEVER fade the overlay to transparent. Reset the
      // zoom instantly while it remains opaque, replace the decoded source, then
      // animate the subtle zoom again. The featured first image cannot flash here.
      image.style.transition = 'none';
      image.style.opacity = '1';
      image.style.transform = 'scale(1.012)';
      image.src = src;
      void image.offsetWidth;
      image.style.transition = `opacity ${FADE_MS}ms ease, transform ${ZOOM_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (mySwap !== swapToken || !overlay || overlay !== image) return;
          image.style.opacity = '1';
          image.style.transform = `scale(${ZOOM_SCALE})`;
        });
      });
    }

    async function startGallery(mySession) {
      const images = await getImages(productUrl);
      if (session !== mySession || !card.matches(':hover') || images.length < 2) return;

      activeImages = images;
      const baseKey = normalizeImageKey(baseImage.currentSrc || baseImage.src);
      const baseIndex = activeImages.findIndex((src) => normalizeImageKey(src) === baseKey);
      index = baseIndex >= 0 ? (baseIndex + 1) % activeImages.length : 1;

      showImage(activeImages[index]);
      cycleTimer = window.setInterval(() => {
        if (!card.matches(':hover') || !activeImages.length) {
          reset();
          return;
        }
        index = (index + 1) % activeImages.length;
        showImage(activeImages[index]);
      }, IMAGE_INTERVAL);
    }

    card.addEventListener('mouseenter', () => {
      reset();
      const mySession = session;

      // Start fetching immediately so the second image can appear within one second.
      getImages(productUrl);
      hoverTimer = window.setTimeout(() => startGallery(mySession), HOVER_DELAY);
    });

    card.addEventListener('mouseleave', reset);
  }

  function initAll(root = document) {
    if (root.matches?.('.vxhome .vx-product')) initCard(root);
    root.querySelectorAll?.('.vxhome .vx-product').forEach(initCard);
  }

  initAll();

  const hoverObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) initAll(node);
      }
    }
  });

  hoverObserver.observe(document.body, { childList: true, subtree: true });
})();
